/**
 * Platform — the deployment's own records, which belong to no organization,
 * under the top-level `platform` collection. No client writes any of them;
 * only the Admin SDK does. Of the records declared here, `platform/legal`
 * alone is client-readable; every other one declares zero actions, so the
 * emitted rules carry no `match` block for its path and the default-deny tail
 * covers it. `platform/licence` (licence.entity.ts) is member-readable.
 *
 * `platform/mail` — the deployment's Lettermint account: the sender of a
 * sign-in code no organization sends, and the account every organization's
 * mail (tickets, invitations, handovers, codes) sends through until that
 * organization saves a Lettermint credential of its own, which then
 * overrides it. Read by the send seams (settings/lettermint.api.ts) and by
 * founding, which copies `sender` onto the new card; written by the seed and
 * by operators.
 *
 * Fields justified:
 *   sender              — the From address mail on this account sends from,
 *                         on the domain verified with it; the same vocabulary
 *                         as a workspace card's email.sender. An
 *                         organization's mail on this account carries the
 *                         organization's brand name beside it.
 *   projectTokenCipher  — the deployment's Lettermint project token sealed
 *                         under the platform data key (`platform/key`), as an
 *                         organization's token is sealed under its workspace
 *                         key; the send seams open it with platformDecrypt.
 *                         The plain token never touches Firestore: the seed
 *                         or an operator writes this field through the
 *                         platform seal.
 *   signingSecretCipher — the signing secret of this account's delivery-status
 *                         webhook registration (`lettermintWebhook?account=
 *                         platform`), sealed the same way: what authenticates
 *                         the delivered/opened/bounced events of mail an
 *                         organization sent on this account, attributed by
 *                         the orgId the message's metadata carries. Absent,
 *                         that endpoint refuses every request and such mail
 *                         stays "sent, unknown".
 *
 * `platform/payments` — the deployment's Revolut sandbox key pair, the
 * credential every organization starts on until it configures its own. The
 * seed writes it locally from `.secret.local`'s REVOLUT_SANDBOX_*; an
 * operator writes it in production through the same platform seal. Checkout
 * falls back to it only when an organization holds no `revolutSandbox`
 * credential of its own, and the organization's saved credential overrides
 * it. It is never used for a live environment.
 *
 * Fields justified:
 *   publicKey       — the sandbox publishable key the widget loads with; the
 *                     same field as a workspace card's `payment.publicKey`,
 *                     and as nullable as it: absent, the wallet row collapses
 *                     and the card path stands alone.
 *   secretKeyCipher — the sandbox Merchant API secret key (the `secretKey`
 *                     an organization's own `revolutSandbox` credential
 *                     holds) sealed under the platform data key, as
 *                     projectTokenCipher is; checkout opens it with
 *                     platformDecrypt. The plain key never touches Firestore,
 *                     and the schema is strict so a plain `secretKey` written
 *                     beside it is refused.
 * Excluded:
 *   live keys      — a live credential is always the organization's own.
 *   signingSecrets — provider-issued per webhook registration, written by
 *                    that registration seam on the organization's credential.
 *
 * `platform/founding` — who may found an organization by domain alone: an
 * address whose domain is listed. Read by the admission decision (sendOTP,
 * resolveEmail) and by verifyOTP to admit such an address at platform-scope
 * sign-in, and by the foundOrganization callable to found for it; written by
 * the seed and by operators. An address staff approve founds instead through
 * `foundWithOwnerInvite`'s owner invitation (database/access.md, Founding).
 *
 * Fields justified:
 *   domains — the email domains whose addresses may found. A listed domain
 *             admits `*@<domain>` exactly: the address's domain, normalized
 *             the same way, equals an entry; a subdomain is another entry.
 *             Empty is a valid list and means no address founds by domain,
 *             as an absent record does.
 *
 * `platform/legal` — the deployment operator's legal identity and the
 * operator's own platform terms and privacy notice: the party a founder
 * contracts with — on the bare-host consent step, before any organization
 * exists, and on accepting the owner invitation a staff approval founded an
 * organization with, which acceptInvitation refuses without them. Public read: the fields are the operator's Impressum (§ 5 DDG),
 * public by statute, and the bare host's sign-in collects an address before
 * any session exists, so the operator and its privacy notice are owed to a
 * signed-out reader. Written by the seed and by operators through the Admin
 * SDK. A missing record is "absent" (`PlatformLegalSnapshotSchema`), and
 * the consent step refuses acceptance on it; an incomplete one throws.
 *
 * Fields justified:
 *   legalName…phone — `OperatorIdentitySchema`, the workspace card's own
 *                     `legal` block, so one identity renderer reads either.
 *   contactEmail    — required here, nullish on the card: the contracting
 *                     party's contact channel (§ 5 Abs. 1 Nr. 2 DDG) and no
 *                     stored platform record predates it.
 *   terms / privacy — the operator's published text, as the content address
 *                     a workspace card's `legalContent` uses: a version stamp
 *                     plus each locale's sha-256 over its source text, the
 *                     text itself at `platformLegalBlobPath(kind, locale,
 *                     sha)`. Immutable bytes per address, so a consent record
 *                     naming a version and hash names exactly the text shown.
 * Excluded:
 *   brandName / domain — the rendered identity's other two fields come from
 *                     the host the founder is on, not a stored fact.
 *   acceptance        — a founder's consent is the founder's record, not the
 *                     operator's.
 *
 * `platform/key` — the platform data key, stored only wrapped by the Cloud
 * KMS wrapping key: what seals a platform-scope sign-in code and derives the
 * hmac key behind an address's keyed id (`platformHmac`). The same
 * stored shape as a workspace's data key, so it parses with
 * `WorkspaceKeySchema` rather than a second declaration of it.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { LegalDocumentKind, Locale } from "@momo/i18n";
import { z } from "zod";
import { HostnameSchema } from "./domains/domain.entity.ts";
import {
	type Actor,
	LegalContentPointerSchema,
	OperatorIdentitySchema,
} from "./workspace.entity.ts";
import { WorkspaceKeySchema } from "./workspace-key.entity.ts";

export const PlatformMailSchema = z.object({
	sender: z
		.string()
		.email()
		.describe(
			"the From address mail on the deployment's Lettermint account sends from",
		),
	projectTokenCipher: z
		.string()
		.min(1)
		.describe(
			"the deployment's Lettermint project token, ciphertext under the platform data key",
		),
	signingSecretCipher: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the deployment's Lettermint delivery-webhook signing secret, ciphertext under the platform data key",
		),
});
export type PlatformMail = z.infer<typeof PlatformMailSchema>;

export const PlatformMail = entity<Actor>()({
	schema: PlatformMailSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/mail" }));

export const PlatformPaymentsSchema = z
	.object({
		publicKey: z
			.string()
			.min(1)
			.nullish()
			.describe("the deployment's Revolut sandbox publishable key"),
		secretKeyCipher: z
			.string()
			.min(1)
			.describe(
				"the deployment's Revolut sandbox Merchant API secret key, ciphertext under the platform data key",
			),
	})
	.strict();
export type PlatformPayments = z.infer<typeof PlatformPaymentsSchema>;

export const PlatformPayments = entity<Actor>()({
	schema: PlatformPaymentsSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/payments" }));

// Each entry is a hostname identity: trimmed, lower-cased, IDNA-converted, and
// refused with an "@", a leading dot, a path or a port. Two spellings of one
// domain are one entry, so the list keeps the first.
export const PlatformFoundingSchema = z.object({
	domains: z
		.array(HostnameSchema)
		.transform((domains) => [...new Set(domains)])
		.describe("the email domains whose addresses may found an organization"),
});
export type PlatformFounding = z.infer<typeof PlatformFoundingSchema>;
export type PlatformFoundingInput = z.input<typeof PlatformFoundingSchema>;

export const PlatformFounding = entity<Actor>()({
	schema: PlatformFoundingSchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/founding" }));

export const PlatformLegalSchema = OperatorIdentitySchema.extend({
	contactEmail: z
		.string()
		.email()
		.describe("the address a founder or authority reaches the operator at"),
	terms: LegalContentPointerSchema.describe(
		"the content address of the operator's published platform terms",
	),
	privacy: LegalContentPointerSchema.describe(
		"the content address of the operator's published platform privacy notice",
	),
});
export type PlatformLegal = z.infer<typeof PlatformLegalSchema>;

/** `platform/legal`'s snapshot data: undefined when the record is missing,
 *  read as null — absent, so the consent step refuses acceptance. */
export const PlatformLegalSnapshotSchema =
	PlatformLegalSchema.nullish().transform((legal) => legal ?? null);

/** The Storage object holding one locale of the operator's published text. */
export function platformLegalBlobPath(
	kind: LegalDocumentKind,
	locale: Locale,
	sha256: string,
): string {
	return `platform/legal/${kind}/${locale}/${sha256}.json`;
}

export const PlatformLegal = entity<Actor>()({
	schema: PlatformLegalSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, and }) => [can("read").when(() => and())])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/legal" }));

export const PlatformKey = entity<Actor>()({
	schema: WorkspaceKeySchema,
	actions: [] as const,
})
	.pipe(() => [])
	.adaptor(firestore({ hierarchicalGrants: true, path: "platform/key" }));
